import React, { ChangeEvent, useState } from 'react';
import { useApiClient } from '../../services/api-client';
import { useMutation } from '@tanstack/react-query';
import { IManagementAPISendEmailResponse } from '../../types/management-api/policy/sendEmail/response';
import { transformSendEmailRequest } from '../../transformers/policies/request/transformSendEmailRequest';
import { ISelectedRecord } from '../../types/selectedRecord';
import { Plus, Minus } from 'lucide-react';

interface ComponentProps {
  selectedRecord: ISelectedRecord;
}

const Email: React.FC<ComponentProps> = ({ selectedRecord }) => {
  type EmailMode = 'SINGLE' | 'MULTIPLE';
  type MessageStatus = 'SUCCESS' | 'ERROR' | 'NOT_SENT';
  const [checkedEmailBox, setCheckedEmailBox] = useState<EmailMode>('SINGLE');
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [message, setMessage] = useState<MessageStatus>('NOT_SENT');

  const addEmailAddress = () => {
    setEmailAddresses([...emailAddresses, '']);
  };

  const removeEmailAddress = () => {
    setEmailAddresses(emailAddresses.slice(0, -1));
  };

  const handleEmailChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const values = [...emailAddresses];
    values[index] = event.target.value;
    setEmailAddresses(values);
  };

  const apiClient = useApiClient();

  const emailMutation = useMutation<IManagementAPISendEmailResponse, Error>({
    mutationFn: () => {
      return apiClient.post(
        '/policy/email/send',
        transformSendEmailRequest({
          certificateId: selectedRecord.policyId,
          emailAddresses,
        })
      );
    },
    onSuccess() {
      setMessage('SUCCESS');
    },
    onError() {
      setMessage('ERROR');
    },
  });

  const renderEmailMode = (
    mode: EmailMode,
    label: string,
    description?: string,
    selectedEmail?: string
  ) => {
    return (
      <div className="flex flex-row items-center gap-x-4">
        <input
          type="checkbox"
          id="resend-single-checkbox"
          className="h-5 w-5 hover:cursor-pointer"
          value={mode}
          checked={checkedEmailBox === mode}
          onChange={() => {
            setCheckedEmailBox(mode);
            if (mode === 'SINGLE') {
              setEmailAddresses([]);
            }
            if (mode === 'MULTIPLE') {
              setEmailAddresses(['']);
            }
          }}
        />
        <div
          className={`flex 
            ${checkedEmailBox === mode ? 'text-black' : 'text-gray-500 opacity-50'} 
            ${mode === 'SINGLE' ? 'flex-col' : 'flex-row items-center gap-x-1'}
          `}
        >
          <h4 className="text-base font-bold">{label}</h4>
          <p className="text-sm">
            {description}
            <span className="underline">{selectedEmail}</span>
          </p>
          {mode === 'MULTIPLE' && (
            <div className="flex gap-x-1">
              {emailAddresses.length < 5 ? (
                <button
                  className={`text-white font-medium rounded-lg text-sm p-2
                    ${checkedEmailBox === 'MULTIPLE' ? 'bg-ingle-light-purple hover:bg-ingle-dark-purple hover:cursor-pointer' : 'bg-gray-400'} 
                  `}
                  onClick={addEmailAddress}
                >
                  <Plus size={16} strokeWidth={4} />
                </button>
              ) : null}
              {emailAddresses.length > 1 && (
                <button
                  className={`text-white font-medium rounded-lg text-sm p-2
                    ${checkedEmailBox === 'MULTIPLE' ? 'bg-ingle-light-purple hover:bg-ingle-dark-purple hover:cursor-pointer' : 'bg-gray-400'}
                  `}
                  onClick={removeEmailAddress}
                >
                  <Minus size={16} strokeWidth={4} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStatus = (status: MessageStatus) => {
    if (status === 'ERROR') {
      return (
        <div className="p-4 my-4 text-sm text-red-800 rounded-lg bg-red-50">
          There was an error sending the email. Please try again.
        </div>
      );
    }

    if (status === 'SUCCESS') {
      return (
        <div className="p-4 mt-4 text-sm text-green-800 rounded-lg bg-green-50">
          Success - Email sent.
        </div>
      );
    }

    return null;
  };

  const renderEmailInputs = (
    checkedEmailBox: EmailMode,
    emailAddresses: string[]
  ) => {
    if (checkedEmailBox === 'MULTIPLE') {
      return (
        <div>
          {emailAddresses.map((email, index) => (
            <input
              key={index}
              type="email"
              placeholder="Enter email address"
              className="w-full border border-gray-300 rounded-lg p-2 mt-4"
              value={email}
              onChange={e => {
                handleEmailChange(index, e);
              }}
              required
            />
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {renderStatus(message)}

      {message !== 'SUCCESS' && (
        <div>
          {renderEmailMode(
            'SINGLE',
            'Resend policy documents',
            'Send the policy to ',
            selectedRecord.email
          )}

          <hr className="border-gray-300 my-4" />

          {renderEmailMode('MULTIPLE', 'Send to another address')}

          <form
            onSubmit={event => {
              event.preventDefault();
              emailMutation.mutate();
            }}
          >
            {renderEmailInputs(checkedEmailBox, emailAddresses)}

            <div className="flex w-full mt-6">
              <button
                className={`w-full text-white focus:ring-4 focus:ring-blue-300 font-bold rounded-lg text-md px-5 py-3
                      ${
                        emailMutation.isPending
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-ingle-light-purple hover:bg-ingle-dark-purple hover:cursor-pointer'
                      }
                  `}
                disabled={emailMutation.isPending}
              >
                Resend
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Email;
